// The pay floors one service (ProviderWorkProfile) publishes: an hourly floor,
// an annual floor, or both (docs/superpowers/specs/
// 2026-09-29-services-two-pay-floors-design.md at the workspace root). Client
// twin of backend/src/provider-availability/service-rate-types.util.ts: the
// same rule, and the same cases in both test files.
//
// `rateTypes` names them. `rateType` stays and is always the LEAD floor:
// 'hourly' when hourly is published, else 'salary'. Every reader that branches
// on `rateType === 'salary'` therefore keeps working, and its hourly branch
// serves a two-floor service too. A row saved before two floors existed has no
// `rateTypes` and reads as its single `rateType`, so no migration is needed.
//
// An amount is a floor only when its type is published. Stored rows can carry
// a stale second amount, so "amount present" never means "published": go
// through publishedServiceFloors.
//
// Client only: payPatch keeps a draft's rateTypes and rateType in step with its
// amounts (a floor is published exactly when it has a number), and alsoAnnual
// is the annual figure a lead-hourly service prints after its hourly one.
const TYPES = ['hourly', 'salary'];

const positive = (v) => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
};

// The known pay types in `v`, hourly first; anything else is dropped.
const validTypes = (v) => (Array.isArray(v) ? TYPES.filter((t) => v.includes(t)) : []);

// The floors a service publishes, hourly first.
export function serviceRateTypes(s) {
  const stated = validTypes(s?.rateTypes);
  return stated.length ? stated : [s?.rateType === 'salary' ? 'salary' : 'hourly'];
}

// The floor shown and read first: hourly whenever it is published.
export const leadRateType = (types) => (types.includes('hourly') ? 'hourly' : 'salary');

// The published floor amounts; null where a floor is unpublished or unset.
export function publishedServiceFloors(s) {
  const types = serviceRateTypes(s);
  return {
    hourly: types.includes('hourly') ? positive(s?.minHourlyRate) : null,
    annual: types.includes('salary') ? positive(s?.minSalary) : null,
  };
}

// What a pay control's `patch` (one amount, or null to clear it) does to a
// draft: the patch itself, plus the floors that now have a number and the lead
// among them. With no floor left it keeps the prior rateType.
export function payPatch(draft = {}, patch = {}) {
  const next = { ...draft, ...patch };
  const types = TYPES.filter((t) => positive(t === 'hourly' ? next.minHourlyRate : next.minSalary) != null);
  return {
    ...patch,
    rateTypes: types,
    rateType: types.length ? leadRateType(types) : next.rateType === 'salary' ? 'salary' : 'hourly',
  };
}

// The annual figure a lead-hourly service also publishes; null when there is
// none, and null when the rate is withheld.
export function alsoAnnual(schedule) {
  if (!schedule || schedule.rateWithheld || schedule.rateType === 'salary') return null;
  return publishedServiceFloors(schedule).annual;
}

export const annualText = (n) => `$${Number(n).toLocaleString('en-US')}/yr`;

export const annualShort = (n) => `$${Math.round(Number(n) / 1000)}K/yr`;
